import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  StyleSheet,
  ScrollView,
  View,
  RefreshControl,
  Pressable,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import MetaHead from '@/components/MetaHead';
import CatalogDiscoveryHero from '@/components/Catalog/CatalogDiscoveryHero';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import StatusContainer from '@/components/StatusContainer';
import ToolHistoryCard from '@/components/Tools/ToolHistoryCard';
import ToolHistoryRow from '@/components/Tools/ToolHistoryRow';
import ToolHistorySortDropdown from '@/components/Dropdowns/ToolHistorySortDropdown';
import { useToolStore, useToastStore, useAuthStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';

const EMPTY_RUNS = [];

const SECTION_META = {
  today:     { titleKey: 'pages.tools.historySectionToday',     fallback: 'Today',         icon: 'sun' },
  yesterday: { titleKey: 'pages.tools.historySectionYesterday', fallback: 'Yesterday',     icon: 'moon' },
  thisWeek:  { titleKey: 'pages.tools.historySectionThisWeek',  fallback: 'This week',     icon: 'calendar-week' },
  thisMonth: { titleKey: 'pages.tools.historySectionThisMonth', fallback: 'This month',    icon: 'calendar' },
  earlier:   { titleKey: 'pages.tools.historySectionEarlier',   fallback: 'Earlier',       icon: 'clock-rotate-left' },
};
const SECTION_ORDER = ['today', 'yesterday', 'thisWeek', 'thisMonth', 'earlier'];

const STATUS_SCOPES = [
  { id: 'all',       labelKey: 'pages.tools.statusFilter_all',       fallback: 'All',         icon: 'list',               colorKey: 'primary' },
  { id: 'completed', labelKey: 'pages.tools.statusFilter_completed', fallback: 'Completed',   icon: 'circle-check',       colorKey: 'success' },
  { id: 'failed',    labelKey: 'pages.tools.statusFilter_failed',    fallback: 'Failed',      icon: 'circle-exclamation', colorKey: 'danger' },
  { id: 'running',   labelKey: 'pages.tools.statusFilter_running',   fallback: 'In flight',   icon: 'spinner',            colorKey: 'warning' },
];

const SCOPE_COLORS = {
  primary: null, // use theme text_color_primary
  success: '#22c55e',
  danger:  '#ef4444',
  warning: '#f59e0b',
};

const HASH_PALETTE = [
  '#8B5CF6', '#3B82F6', '#22C55E', '#F59E0B',
  '#EC4899', '#F97316', '#14B8A6', '#06B6D4',
  '#A855F7', '#10B981',
];

function accentForToolSlug(slug) {
  if (!slug || slug === 'all') return null;
  let hash = 0;
  const s = String(slug);
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return HASH_PALETTE[hash % HASH_PALETTE.length];
}

function runMatchesStatus(run, statusFilter) {
  if (statusFilter === 'all') return true;
  if (statusFilter === 'running') return run.status === 'running' || run.status === 'pending';
  return run.status === statusFilter;
}

function runMatchesTool(run, toolFilter) {
  if (toolFilter === 'all') return true;
  const slug = run?.tool?.slug || run?.toolSlug;
  return slug === toolFilter;
}

// Bucket runs into roughly human-friendly groups so the grid breathes a bit
// more than a single endless wall of cards. Returns ordered groups so the
// most-recent activity is always on top.
function groupRunsByDate(runs) {
  const now    = new Date();
  const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yest   = today - 86400000;
  const weekAgo = today - 7 * 86400000;
  const monthAgo = today - 30 * 86400000;

  const buckets = { today: [], yesterday: [], thisWeek: [], thisMonth: [], earlier: [] };
  for (const run of runs) {
    const ts = run?.createdAt ? new Date(run.createdAt).getTime() : 0;
    if (ts >= today)        buckets.today.push(run);
    else if (ts >= yest)    buckets.yesterday.push(run);
    else if (ts >= weekAgo) buckets.thisWeek.push(run);
    else if (ts >= monthAgo) buckets.thisMonth.push(run);
    else                    buckets.earlier.push(run);
  }
  return buckets;
}

// Build a single lowercase haystack per run for cheap substring searching.
function buildSearchHaystack(run) {
  const parts = [];
  if (run?.tool?.name) parts.push(run.tool.name);
  if (run?.tool?.slug) parts.push(run.tool.slug);
  if (run?.toolSlug) parts.push(run.toolSlug);
  if (run?.compiledPrompt) parts.push(run.compiledPrompt);
  if (run?.resultText) parts.push(run.resultText);
  if (Array.isArray(run?.resultSegments)) {
    for (const seg of run.resultSegments) {
      if (seg?.content) parts.push(seg.content);
    }
  }
  if (run?.inputs && typeof run.inputs === 'object') {
    try { parts.push(JSON.stringify(run.inputs)); } catch (_) { /* noop */ }
  }
  if (Array.isArray(run?.resultFiles)) {
    for (const f of run.resultFiles) {
      if (f?.name) parts.push(f.name);
    }
  }
  return parts.join(' ').toLowerCase();
}

// ─── Styles ───────────────────────────────────────────────────────────────

const createStyles = (colors, isXS, isSM, isXL, insetBottom = 0) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.solid_color_primary },
  scroll:    { flex: 1, width: '100%' },
  scrollContent: { width: '100%', paddingBottom: insetBottom + 60 },

  contentWrap: {
    width: '100%',
    maxWidth: isXL ? 1460 : 1240,
    alignSelf: 'center',
  },

  inner: {
    paddingTop: 4,
    width: '100%',
  },

  section: { marginBottom: 28 },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  sectionCount: {
    paddingVertical: 1,
    paddingHorizontal: 7,
    borderRadius: 999,
    backgroundColor: colors.solid_color_block,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isXS ? 12 : 14,
    width: '100%',
  },
  gridItem: {
    flexBasis: isXS
      ? '100%'
      : isSM
        ? Platform.select({ web: 'calc(50% - 7px)', default: '49%' })
        : isXL
          ? Platform.select({ web: 'calc(20% - 12px)', default: '18.5%' })
          : Platform.select({ web: 'calc(25% - 11px)', default: '23%' }),
    minWidth: 140,
    maxWidth: isXS ? '100%' : 320,
    flexGrow: 0,
    flexShrink: 1,
  },
  list: {
    width: '100%',
    gap: 8,
  },

  loadingBlock: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  emptyHero: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
    gap: 14,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.border_color_secondary,
    marginTop: 12,
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(124, 58, 237, 0.12)',
  },
  ctaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 999,
    backgroundColor: 'rgba(124, 58, 237, 1)',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  clearBtn: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
});

export default function ToolHistoryPage() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM, isMD, isXL } = useResponsiveness();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toolFilter,   setToolFilter]   = useState('all');
  const [sortOrder,    setSortOrder]    = useState('newest');
  const [viewMode,     setViewMode]     = useState('grid');

  const allRuns        = useToolStore(s => s.runsByToolSlug.__all__) || EMPTY_RUNS;
  const fetchRuns      = useToolStore(s => s.fetchRuns);
  const isLoading      = useToolStore(s => s.isLoadingRunsByToolSlug?.__all__);
  const error          = useToolStore(s => s.runsErrorByToolSlug?.__all__);
  const deleteRun      = useToolStore(s => s.deleteRun);
  const addToast       = useToastStore(s => s.actions.addToast);
  const userId         = useAuthStore(s => s.user?.id);
  const hasUser        = Boolean(userId);
  const [deletingRunId, setDeletingRunId] = useState(null);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  useEffect(() => {
    // `fetchRuns({})` returns the user's full run history — skip for
    // anonymous visitors (would 401) and render the sign-in CTA panel
    // below instead.
    if (!hasUser) return;
    fetchRuns({}).catch(() => {});
  }, [fetchRuns, hasUser]);

  const onRefresh = useCallback(async () => {
    if (!hasUser) return;
    setRefreshing(true);
    try {
      await fetchRuns({});
    } finally {
      setRefreshing(false);
    }
  }, [fetchRuns, hasUser]);

  const toolScopeRows = useMemo(() => {
    const seen = new Map();
    for (const r of allRuns) {
      const slug = r?.tool?.slug || r?.toolSlug;
      if (!slug || seen.has(slug)) continue;
      seen.set(slug, {
        id: slug,
        label: r?.tool?.name || slug,
        icon: r?.tool?.icon || 'wand-magic-sparkles',
      });
    }
    return Array.from(seen.values());
  }, [allRuns]);

  const haystacks = useMemo(() => {
    const map = new Map();
    for (const r of allRuns) {
      const id = r?.id || r?._id;
      if (!id) continue;
      map.set(id, buildSearchHaystack(r));
    }
    return map;
  }, [allRuns]);

  // Facet-style counts: status row counts respect the active tool filter;
  // tool row counts respect the active status filter (same refinement UX
  // as the models catalog rail).
  const countRunsForStatus = useCallback((statusId) => {
    return allRuns.filter((r) => {
      if (!runMatchesTool(r, toolFilter)) return false;
      return runMatchesStatus(r, statusId);
    }).length;
  }, [allRuns, toolFilter]);

  const countRunsForToolSlug = useCallback((slug) => {
    if (slug === 'all') {
      return allRuns.filter((r) => runMatchesStatus(r, statusFilter)).length;
    }
    return allRuns.filter((r) => {
      if (!runMatchesStatus(r, statusFilter)) return false;
      const s = r?.tool?.slug || r?.toolSlug;
      return s === slug;
    }).length;
  }, [allRuns, statusFilter]);

  const filteredRuns = useMemo(() => {
    let list = allRuns;

    if (statusFilter !== 'all') {
      list = list.filter(r => runMatchesStatus(r, statusFilter));
    }
    if (toolFilter !== 'all') {
      list = list.filter(r => runMatchesTool(r, toolFilter));
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(r => {
        const id = r?.id || r?._id;
        const hay = id ? haystacks.get(id) : buildSearchHaystack(r);
        return hay && hay.includes(q);
      });
    }

    if (sortOrder === 'oldest') {
      list = [...list].sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return ta - tb;
      });
    } else if (sortOrder === 'newest') {
      list = [...list].sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
    }

    return list;
  }, [allRuns, statusFilter, toolFilter, searchQuery, sortOrder, haystacks]);

  const groups = useMemo(() => groupRunsByDate(filteredRuns), [filteredRuns]);

  const handleOpenRun = useCallback((run) => {
    const slug = run?.tool?.slug || run?.toolSlug;
    if (!slug || !run?.id) return;
    router.push(`/tools/${slug}?runId=${encodeURIComponent(run.id)}`);
  }, [router]);

  const performDeleteRun = useCallback(async (run) => {
    if (!run?.id) return;
    setDeletingRunId(run.id);
    try {
      await deleteRun(run.id);
    } catch (e) {
      addToast?.(e?.message || 'Failed to delete run', 'error');
    } finally {
      setDeletingRunId(null);
    }
  }, [deleteRun, addToast]);

  const handleDeleteRun = useCallback((run) => {
    if (!run?.id) return;
    const toolPhrase = (run?.tool?.name || run?.toolSlug || '').trim()
      || (t('pages.tools.deleteRunUnknownTool') || 'this tool');
    const title = t('pages.tools.deleteRunTitle') || 'Delete this run?';
    const message = (t('pages.tools.deleteRunMessage') || 'This removes the run from your history for %TOOL%. You cannot undo this.')
      .replace('%TOOL%', toolPhrase);
    showConfirmationModal({
      title,
      message,
      confirmText: t('common.delete') || 'Delete',
      cancelText: t('common.cancel') || 'Cancel',
      isDangerous: true,
      options: {
        height: 180,
        width: 350,
      },
      onConfirm: () => {
        void performDeleteRun(run);
      },
    });
  }, [t, showConfirmationModal, performDeleteRun]);

  const clearAllFilters = useCallback(() => {
    setStatusFilter('all');
    setToolFilter('all');
    setSearchQuery('');
  }, []);

  const isFiltering = statusFilter !== 'all' || toolFilter !== 'all' || !!searchQuery.trim();
  const hasAnyRuns = allRuns.length > 0;
  const hasNoMatches = hasAnyRuns && filteredRuns.length === 0;

  const statusRowColor = useCallback((scope) => {
    const key = scope.colorKey;
    if (key === 'primary') return colors.text_color_primary || '#3B82F6';
    return SCOPE_COLORS[key] || '#3B82F6';
  }, [colors]);

  const catalogHistoryHeroItems = useMemo(() => {
    const ts = (r) => new Date(r?.createdAt || 0).getTime();
    return [...allRuns]
      .sort((a, b) => ts(b) - ts(a))
      .slice(0, 6)
      .map((r) => ({
        id: r?.id || r?._id,
        name: r?.tool?.name || r?.toolSlug || 'Run',
        icon: r?.tool?.icon || 'wand-magic-sparkles',
        _run: r,
      }));
  }, [allRuns]);

  const historyBrowseSplitGap = useMemo(
    () => (isXS ? 12 : isSM ? 16 : isMD ? 20 : 24),
    [isXS, isSM, isMD],
  );

  const historyBrowseRailWidth = useMemo(
    () => (isSM ? 200 : isMD ? 230 : 260),
    [isSM, isMD],
  );

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isXL, insets.bottom),
    [colors, isXS, isSM, isXL, insets.bottom],
  );

  const historyBrowsePinned = useMemo(
    () => STATUS_SCOPES.map((scope) => ({
      key: `status:${scope.id}`,
      label: t(scope.labelKey) || scope.fallback,
      icon: scope.icon,
      color: statusRowColor(scope),
      active: statusFilter === scope.id,
      count: countRunsForStatus(scope.id),
      onPress: () => setStatusFilter(scope.id),
    })),
    [t, statusFilter, countRunsForStatus, statusRowColor],
  );

  const historyBrowseCategories = useMemo(() => ({
    headerLabel: (t('pages.tools.historyToolsEyebrow') || 'Tools').toUpperCase(),
    facets: [
      {
        key: 'tool:all',
        label: t('pages.tools.historyAllTools') || 'All tools',
        active: toolFilter === 'all',
        count: countRunsForToolSlug('all'),
        onPress: () => setToolFilter('all'),
        railTint: {
          fg: colors.text_color_primary,
          rowBg: `${colors.text_color_primary}1F`,
        },
        pillTint: {
          fg: colors.text_color_primary,
          rowBg: `${colors.text_color_primary}1F`,
        },
      },
      ...toolScopeRows.map((row) => {
        const accent = accentForToolSlug(row.id) || colors.text_color_primary;
        return {
          key: `tool:${row.id}`,
          label: row.label,
          active: toolFilter === row.id,
          count: countRunsForToolSlug(row.id),
          onPress: () => setToolFilter(row.id),
          railTint: { fg: accent, rowBg: `${accent}1F` },
          pillTint: { fg: accent, rowBg: `${accent}1F` },
        };
      }),
    ],
  }), [t, toolScopeRows, toolFilter, countRunsForToolSlug, colors]);

  const historyBrowseToolbar = useMemo(() => ({
    viewMode,
    listMode: CATALOG_VIEW_MODES.LIST,
    gridMode: CATALOG_VIEW_MODES.GRID,
    onViewModeChange: setViewMode,
    listA11yLabel: t('pages.tools.historyViewList') || 'List view',
    gridA11yLabel: t('pages.tools.historyViewGrid') || 'Grid view',
    trailing: (
      <View style={{
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 10,
        minWidth: 0,
      }}
      >
        <Text size="xs" color="tertiary" style={{ fontVariant: ['tabular-nums'] }}>
          {(t('pages.models.showingResults') || 'Showing %COUNT% of %TOTAL%')
            .replace('%COUNT%', String(filteredRuns.length))
            .replace('%TOTAL%', String(allRuns.length))}
        </Text>
        {isFiltering ? (
          <Pressable onPress={clearAllFilters} style={styles.clearBtn} accessibilityRole="button">
            <Text size="s" weight="semibold" color="primary">
              {t('pages.tools.historyClearFilters') || 'Clear filters'}
            </Text>
          </Pressable>
        ) : null}
        <ToolHistorySortDropdown activeSort={sortOrder} onChange={setSortOrder} />
      </View>
    ),
  }), [
    viewMode,
    t,
    filteredRuns.length,
    allRuns.length,
    isFiltering,
    clearAllFilters,
    sortOrder,
    styles.clearBtn,
  ]);

  const renderHistoryMain = () => (
    <View style={styles.inner}>
      {error && !isLoading && !filteredRuns.length ? (
        <StatusContainer
          type="error"
          message={error}
          actionText={t('pages.common.retry') || 'Retry'}
          onAction={onRefresh}
        />
      ) : isLoading && !hasAnyRuns ? (
        <View style={styles.loadingBlock}>
          <ActivityIndicator size="large" color={colors.text_color_secondary} />
          <Text size="s" color="secondary">
            {t('pages.tools.historyLoading') || 'Loading your runs…'}
          </Text>
        </View>
      ) : !hasAnyRuns ? (
        <View style={styles.emptyHero}>
          <View style={styles.emptyIcon}>
            <Icon name="compass" size={26} color="rgba(124, 58, 237, 1)" solid />
          </View>
          <Text size="m" weight="bold" color="primary">
            {t('pages.tools.historyEmptyTitle') || 'Nothing here yet'}
          </Text>
          <Text size="s" color="secondary" style={{ textAlign: 'center', maxWidth: 380 }}>
            {t('pages.tools.historyEmptyCopy')
              || 'Run any tool and your results will land here automatically — ready to revisit, remix, or re-run with one click.'}
          </Text>
          <Pressable
            style={styles.ctaBtn}
            onPress={() => router.push('/tools')}
            accessibilityRole="button"
          >
            <Icon name="arrow-right" size={12} color="#fff" solid />
            <Text size="s" weight="bold" style={{ color: '#fff' }}>
              {t('pages.tools.browseTools') || 'Browse tools'}
            </Text>
          </Pressable>
        </View>
      ) : hasNoMatches ? (
        <StatusContainer
          type="empty"
          message={searchQuery.trim()
            ? ((t('pages.tools.historyNoSearchMatches') || 'No runs match \u201c%QUERY%\u201d.').replace('%QUERY%', searchQuery.trim()))
            : (t('pages.tools.historyNoMatches') || 'No runs match these filters.')}
          actionText={t('pages.tools.historyClearFilters') || 'Clear filters'}
          onAction={clearAllFilters}
        />
      ) : (
        SECTION_ORDER.filter((key) => groups[key].length > 0).map((key) => {
          const meta = SECTION_META[key];
          const list = groups[key];
          return (
            <View key={key} style={styles.section}>
              <View style={styles.sectionHeader}>
                <Icon name={meta.icon} size={13} color={colors.text_color_secondary} solid />
                <Text size="m" weight="bold" color="primary">
                  {t(meta.titleKey) || meta.fallback}
                </Text>
                <View style={styles.sectionCount}>
                  <Text size="xxs" weight="bold" color="secondary">
                    {list.length}
                  </Text>
                </View>
              </View>
              {viewMode === 'list' ? (
                <View style={styles.list}>
                  {list.map((run) => (
                    <ToolHistoryRow
                      key={run.id}
                      run={run}
                      isDeleting={deletingRunId === run.id}
                      onOpen={handleOpenRun}
                      onDelete={handleDeleteRun}
                    />
                  ))}
                </View>
              ) : (
                <View style={styles.grid}>
                  {list.map((run) => (
                    <View key={run.id} style={styles.gridItem}>
                      <ToolHistoryCard
                        run={run}
                        isDeleting={deletingRunId === run.id}
                        onOpen={handleOpenRun}
                        onDelete={handleDeleteRun}
                      />
                    </View>
                  ))}
                </View>
              )}
            </View>
          );
        })
      )}
    </View>
  );

  if (!hasUser) {
    return (
      <View style={styles.container}>
        <MetaHead title={(t('pages.tools.history') || 'Tool History')} noindex />
        <SignInCTAPanel
          icon="clock-rotate-left"
          source="tools_history"
          title="Sign in to see your tool history"
          subtitle="Every tool you run is saved here so you can revisit, remix, or re-run any output with one click."
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MetaHead title={(t('pages.tools.history') || 'Tool History')} noindex />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode={Platform.OS === 'ios' || Platform.OS === 'android' ? 'on-drag' : 'none'}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        }
      >
        <View style={styles.contentWrap}>
          <CatalogDiscoveryHero
            search={{
              value: searchQuery,
              onChangeText: setSearchQuery,
              placeholder: t('pages.tools.historySearchPlaceholder') || 'Search by tool, prompt, or result…',
            }}
          />
          {hasAnyRuns ? (
            <CatalogBrowseShell
              isXS={isXS}
              splitGap={historyBrowseSplitGap}
              splitPadding={{ paddingLeft: 0, paddingRight: 0 }}
              bodyGap={14}
              railWidth={historyBrowseRailWidth}
              railWebStickyTop={20}
              toolbarPaddingHorizontal={0}
              pinnedItems={historyBrowsePinned}
              categoriesSection={historyBrowseCategories}
              toolbar={historyBrowseToolbar}
            >
              {renderHistoryMain()}
            </CatalogBrowseShell>
          ) : (
            renderHistoryMain()
          )}
        </View>
      </ScrollView>
    </View>
  );
}
